export const PROVIDER_MODES = Object.freeze({
  MANUAL: 'manual',
  HTTP_ADAPTER: 'http_adapter',
  NATIVE_WEBHOOK: 'native_webhook',
});

export const PAYMENT_STATUSES = Object.freeze({
  PENDING_NEXT_STEP: 'pending_next_step',
  CHECKOUT_CREATING: 'checkout_creating',
  CHECKOUT_CREATED: 'checkout_created',
  PROCESSING: 'processing',
  PENDING_CONFIRMATION: 'pending_confirmation',
  PAID: 'paid',
  EXPIRED: 'expired',
  NEEDS_REVIEW: 'needs_review',
  FAILED: 'failed',
});

export const PROVIDER_EVENT_STATUSES = Object.freeze({
  RECEIVED: 'received', PROCESSING: 'processing', PROCESSED: 'processed',
  RETRYABLE: 'retryable', NEEDS_REVIEW: 'needs_review',
});

export const RECIPIENT_STATUSES = Object.freeze({
  NOT_STARTED: 'not_started', ONBOARDING_PENDING: 'onboarding_pending',
  ACTION_REQUIRED: 'action_required', ENABLED: 'enabled', RESTRICTED: 'restricted',
  DISABLED: 'disabled',
});

export const SETTLEMENT_STATUSES = Object.freeze({
  AWAITING_PAYMENT: 'awaiting_payment', SCHEDULED: 'scheduled', ACQUIRING: 'acquiring',
  TRANSFERRING: 'transferring', RELEASED: 'released', HELD: 'held',
  RETRYABLE: 'retryable', NEEDS_REVIEW: 'needs_review',
});

export const STRIPE_FEE_QUOTE_POLICIES = Object.freeze([
  Object.freeze({ policyId: 'stripe-ca-domestic-card-2026-08', label: 'Canada domestic card estimate', providerId: 'stripe', accountRegion: 'CA', currency: 'CAD', settlementCurrency: 'CAD', variableFeeBps: 290, fixedFeeMinor: 30 }),
  Object.freeze({ policyId: 'stripe-us-domestic-card-2026-08', label: 'United States domestic card estimate', providerId: 'stripe', accountRegion: 'US', currency: 'USD', settlementCurrency: 'USD', variableFeeBps: 290, fixedFeeMinor: 30 }),
  Object.freeze({ policyId: 'stripe-ca-usd-to-cad-card-2026-08', label: 'USD cross-border and CAD conversion estimate', providerId: 'stripe', accountRegion: 'CA', currency: 'USD', settlementCurrency: 'CAD', cardOriginAssumption: 'international', estimateAssumption: '2.9% standard card + 0.8% international card + 2% conversion; USD 0.30 conservatively approximates the CAD fixed fee.', variableFeeBps: 570, fixedFeeMinor: 30 }),
  Object.freeze({ policyId: 'stripe-au-domestic-card-2026-08', label: 'Australia domestic card estimate', providerId: 'stripe', accountRegion: 'AU', currency: 'AUD', settlementCurrency: 'AUD', variableFeeBps: 170, fixedFeeMinor: 30 }),
  Object.freeze({ policyId: 'stripe-ie-standard-eea-card-2026-08', label: 'Ireland standard EEA card estimate', providerId: 'stripe', accountRegion: 'IE', currency: 'EUR', settlementCurrency: 'EUR', variableFeeBps: 150, fixedFeeMinor: 25 }),
]);

export const ZERO_FEE_QUOTE_POLICY = Object.freeze({ policyId: 'legacy-zero-v1', label: 'No estimated processor-fee contribution', providerId: 'manual', accountRegion: 'none', currency: '*', settlementCurrency: '*', variableFeeBps: 0, fixedFeeMinor: 0 });
export const UNCONFIGURED_STRIPE_FEE_QUOTE_POLICY_ID = 'stripe-unconfigured';
export const PUBLIC_FEE_QUOTE_POLICY_FIELDS = Object.freeze(['policyId', 'label', 'currency', 'variableFeeBps', 'fixedFeeMinor']);
export const PUBLIC_FEE_QUOTE_DISCLAIMER = 'Best-effort estimate only; card origin, provider pricing, and currency conversion can change actual processor fees.';

export function feeQuotePolicyById(policyId) {
  return STRIPE_FEE_QUOTE_POLICIES.find((policy) => policy.policyId === policyId) || null;
}
export function feeQuotePolicyForInvoice(projectPolicyId, invoiceCurrency) {
  const basePolicy = feeQuotePolicyById(projectPolicyId);
  if (!basePolicy) return ZERO_FEE_QUOTE_POLICY;
  return STRIPE_FEE_QUOTE_POLICIES.find((policy) => policy.accountRegion === basePolicy.accountRegion && policy.settlementCurrency === basePolicy.settlementCurrency && policy.currency === invoiceCurrency) || ZERO_FEE_QUOTE_POLICY;
}
export function defaultFeeQuotePolicyId(currency) {
  return STRIPE_FEE_QUOTE_POLICIES.find((policy) => policy.currency === currency)?.policyId || UNCONFIGURED_STRIPE_FEE_QUOTE_POLICY_ID;
}
export function publicFeeQuotePolicy(policy) {
  return Object.fromEntries(PUBLIC_FEE_QUOTE_POLICY_FIELDS.map((field) => [field, policy[field]]));
}

function safeNonNegativeInteger(value, label) {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`${label} must be a non-negative safe integer.`);
  return value;
}
function ceilBasisPoints(amountMinor, basisPoints) {
  safeNonNegativeInteger(amountMinor, 'Amount');
  safeNonNegativeInteger(basisPoints, 'Basis points');
  if (basisPoints > 10_000) throw new Error('Basis points cannot exceed 10,000.');
  const product = amountMinor * basisPoints;
  if (!Number.isSafeInteger(product)) throw new Error('Money calculation exceeds safe integers.');
  return Math.ceil(product / 10_000);
}
export function calculateProcessorFeeEstimate(chargeTotalMinor, policy) {
  const charge = safeNonNegativeInteger(chargeTotalMinor, 'Charge total');
  const fee = ceilBasisPoints(charge, policy.variableFeeBps) + safeNonNegativeInteger(policy.fixedFeeMinor, 'Fixed fee');
  if (!Number.isSafeInteger(fee)) throw new Error('Processor fee exceeds safe integers.');
  return fee;
}
export function calculatePaymentQuote({ hours, hourlyRateMinor, fixedCostsMinor = 0, flatAdditionMinor, taxRateBps, feeQuotePolicy = ZERO_FEE_QUOTE_POLICY, payerProcessorFeeShareBps = 0 }) {
  if (!Number.isFinite(hours) || hours < 0) throw new Error('Hours must be non-negative.');
  const rate = safeNonNegativeInteger(hourlyRateMinor, 'Hourly rate');
  const fixedMinor = safeNonNegativeInteger(fixedCostsMinor || 0, 'Fixed costs');
  const flatMinor = safeNonNegativeInteger(flatAdditionMinor || 0, 'Payer additional contribution');
  const taxBps = safeNonNegativeInteger(taxRateBps || 0, 'Tax rate');
  const baseMinor = Math.round(hours * rate);
  safeNonNegativeInteger(baseMinor, 'Base amount');
  const contributionMinor = baseMinor + fixedMinor + flatMinor;
  safeNonNegativeInteger(contributionMinor, 'Contribution');
  const taxMinor = ceilBasisPoints(contributionMinor, taxBps);
  const grossBeforeProcessorFeeMinor = contributionMinor + taxMinor;
  safeNonNegativeInteger(grossBeforeProcessorFeeMinor, 'Gross before processor fee');
  let quotedPayerProcessorFeeMinor = 0;
  for (let iteration = 0; iteration < 100; iteration += 1) {
    const chargeTotalMinor = grossBeforeProcessorFeeMinor + quotedPayerProcessorFeeMinor;
    safeNonNegativeInteger(chargeTotalMinor, 'Charge total');
    const nextQuote = ceilBasisPoints(calculateProcessorFeeEstimate(chargeTotalMinor, feeQuotePolicy), payerProcessorFeeShareBps);
    if (nextQuote === quotedPayerProcessorFeeMinor) break;
    if (nextQuote < quotedPayerProcessorFeeMinor || iteration === 99) throw new Error('Processor fee quote did not converge.');
    quotedPayerProcessorFeeMinor = nextQuote;
  }
  const chargeTotalMinor = grossBeforeProcessorFeeMinor + quotedPayerProcessorFeeMinor;
  safeNonNegativeInteger(chargeTotalMinor, 'Charge total');
  return { hours, baseMinor, fixedCostsMinor: fixedMinor, flatAdditionMinor: flatMinor, contributionMinor, taxableMinor: contributionMinor, taxMinor, grossBeforeProcessorFeeMinor, quotedPayerProcessorFeeMinor, chargeTotalMinor, totalMinor: chargeTotalMinor };
}
export function calculateSettlementAmounts({ contributionMinor, taxMinor, quotedPayerProcessorFeeMinor, actualProcessorFeeMinor, payerProcessorFeeShareBps, operatorProcessorFeeShareBps, transferTax }) {
  const contribution = safeNonNegativeInteger(contributionMinor, 'Contribution');
  const tax = safeNonNegativeInteger(taxMinor, 'Tax');
  const quotedFee = safeNonNegativeInteger(quotedPayerProcessorFeeMinor, 'Quoted payer processor fee');
  const actualFee = safeNonNegativeInteger(actualProcessorFeeMinor, 'Actual processor fee');
  if (payerProcessorFeeShareBps + operatorProcessorFeeShareBps !== 10_000) throw new Error('Processor-fee shares must total 10,000 basis points.');
  const targetPayerProcessorFeeMinor = ceilBasisPoints(actualFee, payerProcessorFeeShareBps);
  const operatorProcessorFeeMinor = actualFee - targetPayerProcessorFeeMinor;
  const transferredTaxMinor = transferTax ? tax : 0;
  const retainedTaxMinor = transferTax ? 0 : tax;
  const transferAmountMinor = contribution + transferredTaxMinor - operatorProcessorFeeMinor;
  if (!Number.isSafeInteger(transferAmountMinor) || transferAmountMinor < 0) throw new Error('Settlement transfer amount cannot be negative.');
  return { targetPayerProcessorFeeMinor, operatorProcessorFeeMinor, transferredTaxMinor, retainedTaxMinor, transferAmountMinor, quoteVarianceMinor: quotedFee - targetPayerProcessorFeeMinor };
}
